import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, HourglassMedium, XCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 8) {
        if (!cancelled) setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setBooking(data.booking);
          setState("paid");
          return;
        }
        if (data.payment_status === "pending_verification") {
          setBooking(data.booking);
          setState("submitted");
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const content = {
    checking: { icon: <HourglassMedium size={56} className="text-gold animate-pulse-glow" />, title: "Confirming your payment...", text: "The stars are aligning. This takes just a moment." },
    paid: { icon: <CheckCircle size={56} weight="fill" className="text-work" />, title: "Your session is confirmed", text: booking ? `${booking.package_name} on ${booking.date} at ${booking.time} UTC. A confirmation lives in your dashboard.` : "See your dashboard for details." },
    submitted: { icon: <CheckCircle size={56} weight="duotone" className="text-gold" />, title: "Payment proof received ✧", text: booking ? `${booking.package_name} on ${booking.date} at ${booking.time} UTC. We're verifying your UPI payment — your booking will be confirmed shortly and you'll get an update in your dashboard.` : "We're verifying your UPI payment — check your dashboard shortly." },
    expired: { icon: <XCircle size={56} className="text-red-400" />, title: "Checkout expired", text: "No charge was made. You can book again whenever you're ready." },
    timeout: { icon: <HourglassMedium size={56} className="text-prosper" />, title: "Still processing", text: "Payment confirmation is taking longer than usual. Check your dashboard in a minute." },
    error: { icon: <XCircle size={56} className="text-red-400" />, title: "Something went astray", text: "We couldn't verify this payment session. Check your dashboard or try again." },
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-16" data-testid="booking-success-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="glass-panel p-12 max-w-lg w-full text-center">
        <div className="flex justify-center">{content.icon}</div>
        <h1 className="font-heading text-3xl font-semibold tracking-tighter mt-6" data-testid="payment-status-title">{content.title}</h1>
        <p className="mt-4 text-sm text-muted-foreground leading-relaxed" data-testid="payment-status-text">{content.text}</p>
        <div className="mt-8 flex justify-center gap-4">
          <Link to="/dashboard" data-testid="success-dashboard-link" className="px-6 py-3 rounded-full bg-gold text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#e8c95a] transition-colors duration-300">
            My dashboard
          </Link>
          <Link to="/book" data-testid="success-book-again-link" className="px-6 py-3 rounded-full border border-white/20 text-xs uppercase tracking-[0.2em] hover:border-gold/60 hover:text-gold transition-colors duration-300">
            Book again
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
